import {
  type Dispatch,
  type FunctionComponent,
  type SetStateAction,
  useRef,
} from 'react';

import type { InterviewSegmentT } from 'client/services/ai/realtime';

import { ChatInterface } from 'components/ai-app/ChatInterface';
import type { InputModeT } from 'components/ai-app/ChatInterface/LeftPanel';

import { type UseExperienceResT, useWellbeingAgent } from 'hooks';

import { getRealtimeAiClient } from 'services';
import { processInterview } from 'services/ai';
import { getAiExperience } from 'services/experience';

import {
  type ExperienceAndSessionMetaT,
  type InsightT,
  QuestionnaireKinds,
  type TranscriptT,
  addInsights,
  initExperienceData,
  useGlobalState,
} from 'utils';

/**
 * Human-readable names of the interview branches / stages.
 */
const interviewBranchNames: Record<InterviewSegmentT, string> = {
  '1_LIFE_MOMENT_DEDUCTION': 'Life moment deduction',
  // '2_USER_EVALUATION': 'Evaluation of your state',
  '3_CONTEXT_ENRICHMENT': 'Context enrichment',
};

type HeapT = {
  // TODO: Verify, if it is still used in the interview preparation?
  startedAt: number;
};

type PropsT = {
  experience: UseExperienceResT;
  initialInputMode: InputModeT;

  // NOTE: In this map the keys are IDs of the messages from which
  // the corresponding insights have been extracted.
  insights: Record<string, InsightT[]>;

  onProgress: (progress: number) => void;
  onSignal: (
    signal: 'EXPERIENCE_PREPARATION' | 'EXPERIENCE_READY',
    transcript?: TranscriptT,
  ) => void;
  setInsights: Dispatch<SetStateAction<Record<string, InsightT[]>>>;
  setStageName?: (name: string | undefined) => void;
};

/**
 * The Conversation component is the assembly of the AI chat on the left,
 * and auxiliary side panel on the right (where key insights extracted by AI
 * from the conversation are displayed).
 */
export const Conversation: FunctionComponent<PropsT> = ({
  experience,
  initialInputMode,
  insights,
  onProgress,
  onSignal,
  setInsights,
  setStageName,
}) => {
  const { current: heap } = useRef<HeapT>({
    // TODO: Well... it will do for now... otherwise, we should move it inside
    // the useWellbeingAgent() hook.
    // eslint-disable-next-line react-hooks/purity
    startedAt: Date.now(),
  });

  const [userId] = useGlobalState('user.id');
  if (!userId) throw Error('Unauthenticated');

  const { noAudioInputAfterMessageId, state, transcript } = useWellbeingAgent({
    experience: null,
    mode: 'WELLBEING_CHECK_IN',
    onSignal: (message) => {
      switch (message.kind) {
        case 'CHECK_IN_DONE':
          // BEWARE: Remember, the Agent SDK waits for this function to resolve,
          // considering the resolved value, if any, as the result of the function
          // call to be passed into the agent... if we are to stop the agent,
          // as we do here, we should return the result before that...
          // thats why we trigger the logic asynchronously, and don't wait for it.
          // (and considers the resolved value as the result of the )
          void (async () => {
            // TODO: This is, of course, bad... this function should be moved
            // outside, and perhaps further refactored.
            const realtime = await getRealtimeAiClient();

            const transcript2 = realtime.getCurrentTranscript().slice();

            setStageName?.(undefined);
            onSignal('EXPERIENCE_PREPARATION', transcript2);

            // BEWARE: If called synchronously, it results in the stoppage
            // before the result of this function invokation is delivered
            // to the agent, which prints an error to console... let's just
            // do it like this for now.
            // TODO: I guess, the realtime agent should self-terminate,
            // but for now let's stop it from here (to not mess up with
            // different pieces of the app relying on its previous behavior).
            setTimeout(() => {
              void realtime.stop();
            }, 1);

            // TODO: For now this code is just copied from the Home page,
            // where it probably won't be needed. Otherwise, we need to move
            // it to a dedicated hook / function.
            await experience.reload(
              async (): Promise<ExperienceAndSessionMetaT> => {
                try {
                  const now = Date.now();
                  const userDateTime = Date();

                  const {
                    questionnaireId,
                    transcriptId,
                  } = await processInterview({
                    completedAt: now,
                    kind: QuestionnaireKinds.WELLNESS_CHECKIN__AI_REALTIME,
                    sessionId: undefined,
                    startedAt: heap.startedAt,
                    transcript: transcript2,
                    userDateTime,
                    userId,
                  });

                  const suggestion = await getAiExperience({
                    bodyPositions: message.bodyPositions,
                    checkInTranscriptId: transcriptId,
                    desiredState: message.desiredState,
                    durationMin: message.availableTimeMin,
                    environment: message.environment,
                    excludedArtistIds: message.excludedArtistIds,
                    intent: message.intent,
                    intentGroup: message.intentGroup,
                    lifeMoment: message.lifeMoment,
                    modalities: message.modalities,
                    selectedArtistIds: message.selectedArtistIds,
                    userDateTime,
                  });

                  return { ...suggestion, checkInId: questionnaireId };
                } catch {
                  // TODO: We need a properly styled error modal.
                  // eslint-disable-next-line no-alert
                  alert('Failed to generate experience with AI');

                  return {
                    checkInId: null,
                    experience: initExperienceData(),
                    sessionKind: null,
                  };
                }
              },
            );

            onSignal('EXPERIENCE_READY');
          })();
          break;
        case 'DIRECTIVE_SESSION_REQUEST':
          void (async () => {
            const realtime = await getRealtimeAiClient();
            const transcript2 = realtime.getCurrentTranscript().slice();
            onSignal('EXPERIENCE_PREPARATION', transcript2);

            setTimeout(() => {
              // TODO: In this case we need to save interview & update
              // the profile in a dedicated call (which we'll also use
              // in the normal flow).
              void realtime.stop();
            }, 1);

            await experience.reload(
              async (): Promise<ExperienceAndSessionMetaT> => {
                // TODO: This async operation includes two AI requests - one to
                // normalize the interview transcript to its "quesionnaire" form,
                // then another, to update user profile with the information from
                // the questionnaire. On one hand, this is an unnecessary delay
                // for the direct prescription... on the other, changing it for
                // a different approach (like making processInterview() in
                // background) is somewhat tricky... as we want to save
                // the questionnaire ID to the session object, to be able
                // to retrieve it in the check-out. So... for now, let's just
                // wait here, and optimize for it later, may be.
                const { questionnaireId } = await processInterview({
                  completedAt: Date.now(),
                  kind: QuestionnaireKinds.WELLNESS_CHECKIN__AI_REALTIME,
                  sessionId: undefined,
                  startedAt: heap.startedAt,
                  transcript: transcript2,
                  userDateTime: Date(),
                  userId,
                });

                return {
                  checkInId: questionnaireId,
                  experience: {
                    ...initExperienceData(),
                    videos: message.videos,
                  },
                  sessionKind: null,
                };
              },
            );

            onSignal('EXPERIENCE_READY');
          })();
          break;
        case 'INSIGHT': {
          void (async () => {
            const realtime = await getRealtimeAiClient();
            const transcript2 = realtime.getCurrentTranscript();
            setInsights(
              (old) => addInsights(old, transcript2, message.insights),
            );
          })();
          break;
        }
        case 'INTERVIEW_SEGMENT_CHANGE':
          setStageName?.(interviewBranchNames[message.next]);
          break;
        case 'PROGRESS_UPDATE':
          onProgress(message.progress);
          break;
        case 'END':
        default: throw Error('Unexpected case');
      }
    },
    sessionKind: null,
  });

  return (
    <ChatInterface
      agentState={state}
      initialInputMode={initialInputMode}
      insights={insights}
      noAudioInputAfterMessageId={noAudioInputAfterMessageId}
      transcript={transcript}
    />
  );
};
